import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { baseUrl } from "../../services/AzureStorage";
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";
import { useNavigate } from "react-router-dom";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import GroupCombineStatsRadarChart from "./GroupCombineStatsRadarChart";
import HistoricalCombineStatsBarChart from "./HistoricalCombineStatsBarChart";

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const historicalPercentileShootingColumn = [
  {
    id: "Player Name",
    headerName: "Player Name",
    style: { padding: "4px", paddingLeft: "25px" },
    unit: "",
  },
  {
    id: "Position",
    headerName: "Position",
    style: { padding: "4px", textAlign: "center" },
    unit: "",
  },
  {
    id: "College Name",
    headerName: "College Name",
    style: { padding: "4px", textAlign: "center" },
    unit: "",
  },
  {
    id: "Spot Up Shooting",
    headerName: "Spot Up Shooting",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
  {
    id: "Side Mid Side Drill",
    headerName: "Side Mid Side Drill",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
  {
    id: "Mid Side Mid Drill",
    headerName: "Mid Side Mid Drill",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
  {
    id: "Off The Dribble Shooting",
    headerName: "Off The Dribble Shooting",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
  {
    id: "Free Throws",
    headerName: "Free Throws",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
  {
    id: "3 Point Star Drill",
    headerName: "3 Point Star Drill",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
  {
    id: "Mid Range Star Drill",
    headerName: "Mid Range Star Drill",
    style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    unit: ``,
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "5px 15px",
    border: "1px solid lightgrey",
    borderRadius: "10px",
    boxShadow: "2px 2px 11px grey inset",
  },
  listItemIcon: {
    minWidth: "38px",
  },
  imageIcon: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  iconRoot: {
    textAlign: "center",
    width: "100%",
    height: "50px",
  },
  cardStyle: {
    border: "1px solid lightgrey",
    borderRadius: "10px",
    height: "250px",
  },

  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerText2: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerTextInfo: {
    color: "rgb(29, 66, 138)",
    fontSize: "14px",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  smBlue: {
    marginLeft: "16px",
    width: 26,
    height: 26,
    color: theme.palette.getContrastText("#0c2c75"),
    backgroundColor: "#1e3f7e",
    position: "relative",
    overflow: "hidden",
    borderRadius: "50%",
  },
  playerNameStyle: {
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  dataField: {
    backgroundColor: "#1d428a",
    color: "white",
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "400",
  },
  percentileField: {
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  cellContent: {
    padding: "5px",
    textAlign: "center",
  },
  percentileCellContent: {
    display: "flex",
    justifyContent: "center",
  },
}));

const HistoricalPercentileShootingDrills = (props) => {
  const classes = useStyles();
  let navigate = useNavigate();
  const {
    shootingDrills,
    eventId,
    shootingDrilsRadarData,
    loading,
    primaryPlayers,
    shootingDrilsAVGRadarData,
    shootingDrilsGroupPercentile,
    isShowGroupPercentile,
  } = props;

  //#Destructuring Data
  const { primaryPlayer, secondaryPlayer, groupPercentile } = shootingDrills;

  const {
    data: primaryPlayerData,
    avg: avgPrimary,
    percentile: primaryPercentile,
  } = primaryPlayer;
  const {
    data: secondaryPlayerData,
    avg: avgSecondary,
    percentile: SecondaryPercentile,
  } = secondaryPlayer;

  //#Preaparing Radar Data
  const labels = shootingDrilsRadarData.map((val) => val.category);
  const compareGroup1Data = shootingDrilsRadarData.map((val) =>
    parseFloat(val.primaryPlayerPercentile).toFixed(2)
  );
  const compareGroup2Data = shootingDrilsRadarData.map((val) =>
    parseFloat(val.secondaryPlayerPercentile).toFixed(2)
  );

  //#Preaparing AVG Radar Data
  const avGLabels = shootingDrilsAVGRadarData.map((val) => val.category);
  const aVGCompareGroup1Data = shootingDrilsAVGRadarData.map((val) =>
    parseFloat(val.primaryPlayerAVG).toFixed(2)
  );
  const aVGCompareGroup2Data = shootingDrilsAVGRadarData.map((val) =>
    parseFloat(val.groupAVG).toFixed(2)
  );

  //#Preaparing Group Percentile Radar Data
  const groupPercentileLabels = shootingDrilsGroupPercentile.map(
    (val) => val.category
  );
  const compareGroupPercentile1 = shootingDrilsGroupPercentile.map((val) =>
    parseFloat(val.primaryPlayerPercentile).toFixed(2)
  );
  const compareGroupPercentile2 = shootingDrilsGroupPercentile.map((val) =>
    parseFloat(val.secondaryPlayerPercentile).toFixed(2)
  );

  const viewPlayerProfile = (playerId) => {
    navigate(`/${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`, {
      state: { from: "CombineResults", tab: 0 },
    });
  };

  const showAvgGroup = () =>
    (primaryPlayerData.length === 1 && secondaryPlayerData.length === 1) ||
    !primaryPlayers;

  return (
    <>
      <Container maxWidth={false}>
        <Box p={1}>
          {loading ? (
            <Box sx={{ textAlign: "center", minHeight: "50px" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper elevation={3} sx={{ my: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Group 1
                    </Typography>
                  </Box>
                </Box>
                <Box p={2}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileShootingColumn.map(
                            (col, index) => (
                              <BootstrapTooltip
                                title={col.id}
                                placement="top-start"
                                key={index}
                              >
                                <TableCell style={col.style}>
                                  {col.headerName}
                                </TableCell>
                              </BootstrapTooltip>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {primaryPlayerData.length > 0 ? (
                          <>
                            {/* Display Comparable Group 1 Details for Shooting Drills */}
                            {primaryPlayerData?.map((val, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes.stickyColumn}
                                    sx={{ padding: "4px" }}
                                  >
                                    <Box display="flex">
                                      {val.headshotURL &&
                                      val.headshotURL !== "" ? (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          src={
                                            val.headshotURL
                                              ? baseUrl(val.headshotURL)
                                              : ""
                                          }
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      )}
                                      &nbsp;&nbsp;
                                      <BootstrapTooltip
                                        title="View Player Profile"
                                        placement="right-start"
                                      >
                                        <Typography
                                          variant="body2"
                                          className={classes.playerNameStyle}
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          {val.playerName}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.position ? val.position : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.collegeOrClub
                                      ? val.collegeOrClub
                                      : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeSpotup}/{val.maxCountSpotUp}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeSideMidSide}/
                                    {val.maxCountSideMidSide}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeMidSideMid}/
                                    {val.maxCountMidSideMid}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeOffTheDribble}/
                                    {val.maxCountOffTheDribble}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeFreeThrows}/
                                    {val.maxCountFreeThrows}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMade3PointStar}/
                                    {val.maxCount3PointStar}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeMidRangeStar}/
                                    {val.maxCountMidRangeStar}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {/* Display Comparable Group 1 Average Details for Shooting Drills */}
                            {avgPrimary &&
                            Object.keys(avgPrimary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"AVG"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMadeSpotup}/
                                  {avgPrimary.maxCountSpotUp}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMadeSideMidSide}/
                                  {avgPrimary.maxCountSideMidSide}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMadeMidSideMid}/
                                  {avgPrimary.maxCountMidSideMid}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMadeOffTheDribble}/
                                  {avgPrimary.maxCountOffTheDribble}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMadeFreeThrows}/
                                  {avgPrimary.maxCountFreeThrows}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMade3PointStar}/
                                  {avgPrimary.maxCount3PointStar}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.noOfMadeMidRangeStar}/
                                  {avgPrimary.maxCountMidRangeStar}
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}

                            {/* Display Comparable Group 1 Average Details for Shooting Drills */}
                            {primaryPercentile &&
                            Object.keys(primaryPercentile).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"PERCENTILE"}
                                  </Typography>
                                </TableCell>

                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentageSpotup
                                        ? `${primaryPercentile.percentageSpotup}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentageSideMidSide
                                        ? `${primaryPercentile.percentageSideMidSide}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentageMidSideMid
                                        ? `${primaryPercentile.percentageMidSideMid}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentageOffTheDribble
                                        ? `${primaryPercentile.percentageOffTheDribble}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentagFreeThrows
                                        ? `${primaryPercentile.percentagFreeThrows}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentage3PointStar
                                        ? `${primaryPercentile.percentage3PointStar}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.percentageMidRangeStar
                                        ? `${primaryPercentile.percentageMidRangeStar}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              No Records Found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>

              <Paper elevation={3} sx={{ my: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Group 2
                    </Typography>
                  </Box>
                </Box>
                <Box p={2}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileShootingColumn.map(
                            (col, index) => (
                              <BootstrapTooltip
                                title={col.id}
                                placement="top-start"
                                key={index}
                              >
                                <TableCell style={col.style}>
                                  {col.headerName}
                                </TableCell>
                              </BootstrapTooltip>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {secondaryPlayerData.length > 0 ? (
                          <>
                            {/* Display Comparable Group 2 Details for Shooting Drills */}
                            {secondaryPlayerData?.map((val, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes.stickyColumn}
                                    sx={{ padding: "4px" }}
                                  >
                                    <Box display="flex">
                                      {val.headshotURL &&
                                      val.headshotURL !== "" ? (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          src={
                                            val.headshotURL
                                              ? baseUrl(val.headshotURL)
                                              : ""
                                          }
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      )}
                                      &nbsp;&nbsp;
                                      <BootstrapTooltip
                                        title="View Player Profile"
                                        placement="right-start"
                                      >
                                        <Typography
                                          variant="body2"
                                          className={classes.playerNameStyle}
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          {val.playerName}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.position ? val.position : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.collegeOrClub
                                      ? val.collegeOrClub
                                      : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeSpotup}/{val.maxCountSpotUp}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeSideMidSide}/
                                    {val.maxCountSideMidSide}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeMidSideMid}/
                                    {val.maxCountMidSideMid}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeOffTheDribble}/
                                    {val.maxCountOffTheDribble}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeFreeThrows}/
                                    {val.maxCountFreeThrows}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMade3PointStar}/
                                    {val.maxCount3PointStar}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.noOfMadeMidRangeStar}/
                                    {val.maxCountMidRangeStar}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {/* Display Comparable Group 2 Average Details for Shooting Drills */}
                            {avgSecondary &&
                            Object.keys(avgSecondary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"AVG"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMadeSpotup}/
                                  {avgSecondary.maxCountSpotUp}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMadeSideMidSide}/
                                  {avgSecondary.maxCountSideMidSide}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMadeMidSideMid}/
                                  {avgSecondary.maxCountMidSideMid}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMadeOffTheDribble}/
                                  {avgSecondary.maxCountOffTheDribble}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMadeFreeThrows}/
                                  {avgSecondary.maxCountFreeThrows}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMade3PointStar}/
                                  {avgSecondary.maxCount3PointStar}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.noOfMadeMidRangeStar}/
                                  {avgSecondary.maxCountMidRangeStar}
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}

                            {/* Display Comparable Group 2 Average Details for Shooting Drills */}
                            {SecondaryPercentile &&
                            Object.keys(SecondaryPercentile).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell sx={{ padding: "4px" }}></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"PERCENTILE"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentageSpotup
                                        ? `${SecondaryPercentile.percentageSpotup}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentageSideMidSide
                                        ? `${SecondaryPercentile.percentageSideMidSide}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentageMidSideMid
                                        ? `${SecondaryPercentile.percentageMidSideMid}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentageOffTheDribble
                                        ? `${SecondaryPercentile.percentageOffTheDribble}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentagFreeThrows
                                        ? `${SecondaryPercentile.percentagFreeThrows}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentage3PointStar
                                        ? `${SecondaryPercentile.percentage3PointStar}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {SecondaryPercentile.percentageMidRangeStar
                                        ? `${SecondaryPercentile.percentageMidRangeStar}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              No Records Found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>

              {/* GROUP PERCENTILE SECTION */}
              {isShowGroupPercentile === "true" ? (
                <>
                  <Paper elevation={3} sx={{ my: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      className={classes.headerBox}
                    >
                      <Box display="flex" pl={2}>
                        <Typography
                          className={`${classes.headerText} headerTitle`}
                        >
                          Group Percentile
                        </Typography>
                      </Box>
                    </Box>
                    <Box p={2}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {historicalPercentileShootingColumn.map(
                                (col, index) => (
                                  <BootstrapTooltip
                                    title={col.id}
                                    placement="top-start"
                                    key={index}
                                  >
                                    <TableCell style={col.style}>
                                      {col.headerName}
                                    </TableCell>
                                  </BootstrapTooltip>
                                )
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Display Group Percentile Details for Shooting Drills */}
                            {groupPercentile &&
                            Object.keys(groupPercentile).length > 0 ? (
                              <TableRow>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {""}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentageSpotup
                                        ? `${groupPercentile.percentageSpotup}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentageSideMidSide
                                        ? `${groupPercentile.percentageSideMidSide}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentageMidSideMid
                                        ? `${groupPercentile.percentageMidSideMid}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentageOffTheDribble
                                        ? `${groupPercentile.percentageOffTheDribble}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentagFreeThrows
                                        ? `${groupPercentile.percentagFreeThrows}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentage3PointStar
                                        ? `${groupPercentile.percentage3PointStar}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.percentageMidRangeStar
                                        ? `${groupPercentile.percentageMidRangeStar}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Paper>
                </>
              ) : null}

              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ my: 2 }}
              >
                {/* <Grid item md={6} sm={12} xs={12} justifyContent="center">
                  {shootingDrilsAVGRadarData.length > 0 ? (
                    <GroupCombineStatsRadarChart
                      labels={avGLabels}
                      data={aVGCompareGroup1Data}
                      name={"Group 1"}
                      otherPlayerdata={aVGCompareGroup2Data}
                      nameotherplayer={"Group 2"}
                      chartName={"Historical Group Average "}
                      isPercentileChart={false}
                      max={50}
                    />
                  ) : (
                    ""
                  )}
                </Grid> */}

                <Grid item md={6} sm={12} xs={12} justifyContent="center">
                  {shootingDrilsRadarData.length > 0 ? (
                    // <GroupCombineStatsRadarChart
                    //   labels={labels}
                    //   data={compareGroup1Data}
                    //   name={"Group 1"}
                    //   otherPlayerdata={compareGroup2Data}
                    //   nameotherplayer={"Group 2"}
                    //   chartName={"Historical Group Percentile"}
                    //   max={100}
                    // />
                    <HistoricalCombineStatsBarChart
                      labels={labels}
                      priamryPlayerData={compareGroup1Data}
                      primaryPlayerName={"Group 1"}
                      otherPlayerdata={compareGroup2Data}
                      nameOtherPlayer={"Group 2"}
                      chartName={"Historical Group Percentile"}
                      max={100}
                    />
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>

              {isShowGroupPercentile === "true" ? (
                <>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ my: 2 }}
                  >
                    <Grid item md={6} sm={12} xs={12} justifyContent="center">
                      {shootingDrilsGroupPercentile.length > 0 ? (
                        <HistoricalCombineStatsBarChart
                          labels={groupPercentileLabels}
                          priamryPlayerData={compareGroupPercentile1}
                          primaryPlayerName={"Group 1"}
                          otherPlayerdata={compareGroupPercentile2}
                          nameOtherPlayer={"Group Percentile"}
                          chartName={"Historical Combined Group Percentile"}
                          max={100}
                        />
                      ) : (
                        ""
                      )}
                    </Grid>
                  </Grid>
                </>
              ) : null}
            </>
          )}
        </Box>
      </Container>
    </>
  );
};

export default HistoricalPercentileShootingDrills;
